import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router";
import { tokens } from "../../../../public/tokens";
import PermissionsGate from "../../../../utils/permissionGate";
import useVersionModules from "./hooks/useVersionModules";
import VersionSummaryStripContainer from "./VersionSummaryStripContainer";
import VersionModulesTable from "./versionModules/VersionModulesTable";
import VersionModulesBulkActions from "./versionModules/VersionModulesBulkActions";
import AssignVersionModuleModal from "./versionModules/AssignVersionModuleModal";
import ConfirmationModal from "../linkedProgrammes/ConfirmationModal";

/*
 * Reserves room at the bottom of the page for the fixed-position bulk-action bar,
 * mirroring linkedProgrammes/LinkedProgrammes.styles.js's StyledBulkActionSpacer -
 * without it, the floating "N Modules selected" bar overlaps the table's own last
 * rows/pagination instead of the page just scrolling further to clear it.
 */
const StyledBulkActionSpacer = styled.div(({ $active = false, $height = 0 }) => ({
  width: "100%",
  height: $active
    ? `calc(${Math.max($height, 84)}px + ${tokens.globals["spacing-xl"]})`
    : "0",
  transition: "height 200ms cubic-bezier(0.16, 1, 0.3, 1)",
}));

const StyledTableWrapper = styled.div({
  margin: 0,
  marginTop: tokens.globals["spacing-5"],
  padding: 0,
  overflow: "hidden",
  background: tokens.colors.surfaces["container-background"],
  borderRadius: tokens.globals["border-radius"],
  border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
  boxShadow: tokens.effects.effects.shadow.default,
});

/**
 * Version Modules screen (SPEC3): the version summary strip (with "Assign
 * Module" as its right-hand action), the redesigned modules table with
 * search/filters/sort/pagination and bulk unlink, and the Assign Module modal.
 */
const AssignedTable = (props) => {
  const { versionData } = props;
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const queryVersionId = query.get("versionId");
  const queryProgrammeId = query.get("programmeId");

  const isLocked = Boolean(versionData?.lock_version_settings);

  const [isAssignModuleModalOpen, setIsAssignModuleModalOpen] = useState(false);

  const hook = useVersionModules({
    programmeId: queryProgrammeId,
    versionId: queryVersionId,
    isLocked,
  });

  const bulkActionsBarRef = useRef(null);
  const [bulkActionsBarHeight, setBulkActionsBarHeight] = useState(0);
  const hasSelection = hook.selectedIds.length > 0;

  useLayoutEffect(() => {
    const barNode = bulkActionsBarRef.current;

    if (!hasSelection || !barNode) {
      return undefined;
    }

    const updateHeight = () => {
      setBulkActionsBarHeight(barNode.getBoundingClientRect().height);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(barNode);

    return () => resizeObserver.disconnect();
  }, [hasSelection]);

  return (
    <div>
      <PermissionsGate scopes={["pmadd"]} errorProps={{ actionDisabled: true, actionDisabledReason: "Insufficient Permissions" }}>
        <VersionSummaryStripContainer
          programmeId={queryProgrammeId}
          versionId={queryVersionId}
          isLocked={isLocked}
          onAction={() => setIsAssignModuleModalOpen(true)}
          actionLabel="Assign Module"
          actionIcon="plus"
          actionDisabled={isLocked}
          actionDisabledReason={isLocked ? "This version's settings are locked" : undefined}
        />
      </PermissionsGate>

      <StyledTableWrapper>
        <VersionModulesTable hook={hook} />
      </StyledTableWrapper>

      <StyledBulkActionSpacer $active={hasSelection} $height={bulkActionsBarHeight} />

      <VersionModulesBulkActions hook={hook} barRef={bulkActionsBarRef} />

      <ConfirmationModal
        open={Boolean(hook.confirmation)}
        copy={
          hook.confirmation && {
            title: `Unlink ${hook.confirmation.ids.length} Module${hook.confirmation.ids.length === 1 ? "" : "s"}?`,
            description: "These modules will be removed from this version. This can't be undone.",
            label: "Unlink",
            icon: "unlink",
            tone: "destructive",
          }
        }
        onClose={() => hook.setConfirmation(null)}
        onConfirm={hook.runConfirmedUnlink}
      />

      {isAssignModuleModalOpen && (
        <AssignVersionModuleModal
          programmeId={queryProgrammeId}
          versionId={queryVersionId}
          netsuiteList={hook.netsuiteList}
          isNetsuiteLoading={hook.isNetsuiteLoading}
          moduleNameById={hook.moduleNameById}
          onAssigned={hook.refresh}
          onClose={() => setIsAssignModuleModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AssignedTable;
